/* @flow */

import * as React from 'react';
import {useDispatch} from 'react-redux';

import type {EventFragmentSparse, SessionUser} from 'nutshell-graphql-types';

import {colors} from 'shells/colors';
import type {Lead} from 'shells/timeline/types';

import {appHistory} from '../../../history';
import {openReplyModal} from '../../email/email-reply-actions';
import {EmailRelatedLeadsButton} from '../toolbar/email-related-leads-button';
import {
    getEntryTypeFromPayload,
    getEmailIconVariant,
    getParticipantsForEmail,
    getActorForEmail,
    getStatusForEmail,
    getSubjectForEmail,
    getActionMenuForEmail,
    getAdditionalToolbarOptionsForEmail,
    shouldEmailAppearOnLeadTimeline,
} from '../helpers';
import {EmailMutationWrapper} from '../comments/graphql/email-mutation-wrapper';
import {useCreateComment, useDeleteComment} from '../comments/timeline-comment-mutations';
import {useUpdateEmailLeadAssociations} from '../toolbar/use-update-email-lead-associations';
import {useGetRelatedLeads} from '../graphql/hooks/use-get-related-leads';

import {TimelineEntry} from './timeline-entry';

type OwnProps = {
    event: EventFragmentSparse,
    currentUser: SessionUser,
    onDeleteEvent?: (eventId: string) => Promise<*>,
    hasGutterLine?: boolean,
    isDashboard?: boolean,
    refetchEvents?: ?() => any,
    entityPageId?: ?string,
    onFilterOutEvent?: () => void,
};

type Props = {
    ...OwnProps,
    onRetryFailedEmail: (emailId: string) => any,
    onUpdateEmail: (emailId: string, shouldShare: boolean) => any,
};

export function TimelineEntryEmailComponent(props: Props) {
    const {id, changeType, changeTime, payload, reactions, comments} = props.event;
    const {
        currentUser,
        onRetryFailedEmail,
        onUpdateEmail,
        onDeleteEvent,
        hasGutterLine,
        isDashboard,
        refetchEvents,
        entityPageId,
        onFilterOutEvent,
    } = props;

    const [isViewingAttachments, setIsViewingAttachments] = React.useState<boolean>(false);
    const [isExpanded, setIsExpanded] = React.useState<boolean>(false);

    const createComment = useCreateComment();
    const deleteComment = useDeleteComment();
    const dispatch = useDispatch();
    const {emailRelatedLeads} = useGetRelatedLeads({changeLogId: id});
    const updateEmailRelatedLeadAssociations = useUpdateEmailLeadAssociations(id);

    const onOpenReplyModal = (input: {
        replyToEmailId: string,
        isReplyingAll: boolean,
        onReplySuccess?: () => void,
    }) => {
        dispatch(openReplyModal({...input}));
    };

    const getCustomBorder = (email) => {
        const border = {};
        if (!email.isShared) {
            border.hasDottedBorder = true;
        }
        if (email.isFailed) {
            border.borderColor = colors.red;
        }

        return border;
    };

    /**
     * We need to include this check, even though we are only passing an event
     * with a payload.__typename of 'Email' because Apollo doesn't generate a specific
     * enough type when using a union type (like the TimelineEventPayload).
     * See this issue for more detail:
     * https://github.com/apollographql/apollo-tooling/issues/1223
     */
    if (payload.__typename === 'Email') {
        const disableComments = !payload.isShared;

        const onReply = () => {
            const replyVariables = refetchEvents
                ? {replyToEmailId: payload.id, isReplyingAll: false, onReplySuccess: refetchEvents}
                : {replyToEmailId: payload.id, isReplyingAll: false};

            return onOpenReplyModal(replyVariables);
        };

        const onReplyAll = () => {
            const replyVariables = refetchEvents
                ? {replyToEmailId: payload.id, isReplyingAll: true, onReplySuccess: refetchEvents}
                : {replyToEmailId: payload.id, isReplyingAll: true};

            return onOpenReplyModal(replyVariables);
        };

        const participants = getParticipantsForEmail(
            payload.recipients,
            emailRelatedLeads || undefined
        );
        const relatedLeads: Lead[] =
            emailRelatedLeads && emailRelatedLeads.edges
                ? emailRelatedLeads.edges.map((edge) => ({
                      type: 'leads',
                      id: edge.node.id,
                      name: edge.node.name,
                      avatarUrl: edge.node.avatarUrl,
                      number: edge.node.number,
                      status: edge.node.status,
                      priority: edge.node.priority,
                      htmlUrl: edge.node.htmlUrl,
                      isChecked: edge.isMapped,
                      relatedPerson: edge.node.contacts.primaryEdge
                          ? {
                                type: 'contacts',
                                id: edge.node.contacts.primaryEdge.node.id,
                                name: edge.node.contacts.primaryEdge.node.name,
                                htmlUrl: edge.node.contacts.primaryEdge.node.htmlUrl,
                            }
                          : undefined,
                      relatedCompany: edge.node.accounts.primaryEdge
                          ? {
                                type: 'accounts',
                                id: edge.node.accounts.primaryEdge.node.id,
                                name: edge.node.accounts.primaryEdge.node.name,
                                htmlUrl: edge.node.accounts.primaryEdge.node.htmlUrl,
                            }
                          : undefined,
                  }))
                : [];

        // Check if email entry should be filtered out
        if (onFilterOutEvent && !shouldEmailAppearOnLeadTimeline(emailRelatedLeads, entityPageId)) {
            onFilterOutEvent();
        }

        return (
            <TimelineEntry
                id={id}
                iconVariant={getEmailIconVariant(payload)}
                entryType={getEntryTypeFromPayload(payload)}
                timestamp={changeTime}
                subject={getSubjectForEmail(payload)}
                subjectTitle={payload.subject}
                actor={getActorForEmail(payload.from)}
                status={getStatusForEmail(payload, onRetryFailedEmail, onUpdateEmail)}
                participants={participants}
                relatedLeads={relatedLeads.filter((lead) => lead.isChecked)}
                content={payload.bodyExcerpt}
                changeType={changeType}
                reactions={reactions}
                comments={comments}
                onAddComment={(value) =>
                    createComment({
                        changeLogId: id,
                        parentId: payload.id,
                        value,
                        user: props.currentUser,
                    })
                }
                onDeleteComment={(commentId) => deleteComment({changeLogId: id, commentId})}
                currentUser={currentUser}
                customBorderProps={getCustomBorder(payload)}
                emailEngagement={{
                    attachments: payload.attachments,
                    clicks: payload.trackedClicksCount || 0,
                    opens: payload.trackedViewsCount || 0,
                }}
                disableComments={disableComments}
                actionMenu={getActionMenuForEmail({
                    changeLogId: id,
                    email: payload,
                    onDeleteEvent,
                    onUpdateEmail,
                    onRetryFailedEmail,
                    isViewingAttachments,
                    setIsViewingAttachments,
                    onReply,
                    onReplyAll,
                })}
                additionalToolbarOptions={getAdditionalToolbarOptionsForEmail(payload, {
                    onReply,
                    onReplyAll,
                    expandable: {
                        isExpanded,
                        setIsExpanded,
                    },
                    relatedLeads:
                        isDashboard || !relatedLeads.length
                            ? undefined
                            : {
                                  toolbarButton: (
                                      <EmailRelatedLeadsButton
                                          relatedLeads={relatedLeads}
                                          updateEmailRelatedLeadAssociations={(leadAssociations) =>
                                              updateEmailRelatedLeadAssociations({
                                                  emailId: payload.id,
                                                  leadAssociations,
                                              })
                                          }
                                      />
                                  ),
                              },
                })}
                onClickOpen={() => {
                    appHistory.push(`#${payload.htmlUrlPath}`);
                }}
                isExpanded={isExpanded}
                hasGutterLine={hasGutterLine}
            />
        );
    }

    return null;
}

export function TimelineEntryEmail(props: OwnProps) {
    const emailId = props.event.payload.__typename === 'Email' ? props.event.payload.id : undefined;

    if (emailId) {
        return (
            <EmailMutationWrapper>
                {({updateEmail, retryFailedEmail}) => (
                    <TimelineEntryEmailComponent
                        {...props}
                        onUpdateEmail={(id, shouldShare) => {
                            updateEmail(id, shouldShare);
                        }}
                        onRetryFailedEmail={(id) => {
                            retryFailedEmail(id);
                        }}
                    />
                )}
            </EmailMutationWrapper>
        );
    }

    return null;
}
